import React, { useState, useEffect } from 'react'
import '@/assets/css/audioDesc.css'
import Draggable from 'react-draggable'
import EditClip from '../EditClip/EditClip'
import convertSecondsToCardFormat from '../../../shared/utils/convertSecondsToCardFormat'
import axios from 'axios'
import { toast } from 'react-toastify'
import { Clip } from '@/shared/utils/convertClipObject'
import { YouTubePlayer } from 'youtube-player/dist/types'
import ModalComponent from '../../../shared/components/Modal/Modal'
import { TUTORIAL_TARGETS } from '../../Tutorial/tutorialSelectors'

const getDescriptionDisplay = (clip: Clip): string => {
  if (clip.is_recorded) return clip.description_text || 'Voice recording'
  return clip.description_text || 'No description yet'
}

const getAudioModeConfig = (clip: Clip) => {
  if (clip.is_recorded) {
    return {
      mode: 'voice',
      label: 'My Voice (Recording)',
      icon: 'fa-microphone',
      description: 'Voice recording with optional transcript',
    }
  }
  return {
    mode: 'ai',
    label: 'AI Voice (Text-to-Speech)',
    icon: 'fa-robot',
    description: 'AI-generated voice from text',
  }
}

interface Props {
  userId: string
  youtubeVideoId: string
  unitLength: number
  currentTime: number
  videoLength: number
  updateData: boolean
  setUpdateData: React.Dispatch<React.SetStateAction<boolean>>
  clip: Clip
  setNeedRefresh: React.Dispatch<React.SetStateAction<boolean>>
  setUndoDeletedClip: React.Dispatch<React.SetStateAction<boolean>>
  divWidths: { [key: string]: number }
  handlePlayAudioClip: (startTime: number) => void
  editComponentToggleList: { clipId: string; showEditComponent: boolean }[]
  audioDescriptionId: string
  setEditComponentToggleFunc: (clipId: string, value: boolean) => void
  currentEvent: YouTubePlayer | undefined
  currentState: number
  setShowSpinner: React.Dispatch<React.SetStateAction<boolean>>
  fetchUserVideoData: () => void
  isPreview?: boolean
  setUpdatedDescriptions: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  >
  // Publish props
  isPublished: boolean
  enrollInCollabEdit: boolean
  setEnrollInCollabEdit: (val: boolean) => void
  onPublish: (e: any, checkbox?: boolean) => void
  isTutorialMode?: boolean
  descriptionVolume?: number
}

const AudioClip = ({
  userId,
  youtubeVideoId,
  unitLength,
  currentTime,
  videoLength,
  updateData,
  setUpdateData,
  clip,
  setNeedRefresh,
  divWidths,
  handlePlayAudioClip,
  editComponentToggleList,
  audioDescriptionId,
  setEditComponentToggleFunc,
  currentEvent,
  currentState,
  setShowSpinner,
  fetchUserVideoData,
  isPreview = false,
  setUndoDeletedClip,
  setUpdatedDescriptions,
  isPublished,
  enrollInCollabEdit,
  setEnrollInCollabEdit,
  onPublish,
  isTutorialMode = false,
  descriptionVolume,
}: Props) => {
  const clipID = clip.clip_id
  const clipSequenceNumber = clip.clip_sequence_number
  const clipDescriptionType = clip.description_type
  const initialClipTitle = clip.clip_title?.startsWith('scene undef')
    ? `scene ${clip.description_type}`
    : clip.clip_title

  const initialclipDescriptionText = clip.description_text
  const initialClipPlaybackType = clip.playback_type
  const initialClipStartTime = clip.clip_start_time
  const clipDuration = clip.clip_duration
  const clipAudioPath = clip.clip_audio_path
  const isRecorded = clip.is_recorded
  const clipCreatedAt = clip.createdAt

  const audioModeConfig = getAudioModeConfig(clip)
  const descriptionDisplay = getDescriptionDisplay(clip)

  const [clipDescriptionText, setClipDescriptionText] = useState(
    clip.description_text,
  )
  const [clipPlaybackType, setClipPlayBackType] = useState('')
  const [clipTitle, setClipTitle] = useState('')
  const [clipStartTime, setClipStartTime] = useState(0)
  const [showEditComponent, setShowEditComponent] = useState(false)
  const [adDraggableWidth, setAdDraggableWidth] = useState(0.0)
  const [adDraggablePosition, setAdDraggablePosition] = useState({ x: 0, y: 0 })
  const [isPublishModal, setIsPublishModal] = useState(false)
  const [isYoutubeVideoPlaying, setIsYoutubeVideoPlaying] = useState(
    currentState === 1,
  )

  useEffect(() => {
    setClipPlayBackType(initialClipPlaybackType)
    setClipTitle(initialClipTitle ?? '')
    editComponentToggleList.forEach((item) => {
      if (item.clipId === clipID) {
        item.showEditComponent
          ? setShowEditComponent(true)
          : setShowEditComponent(false)
      }
    })
    setClipStartTime(initialClipStartTime)
    setAdDraggablePosition({ x: initialClipStartTime * unitLength, y: 0 })
    setAdDraggableWidth(clipDuration * unitLength)
    setIsYoutubeVideoPlaying(currentState === 1)
  }, [
    clipDuration,
    clipID,
    currentState,
    initialClipPlaybackType,
    initialClipStartTime,
    initialClipTitle,
    editComponentToggleList,
    unitLength,
  ])

  const stopADBar = (event: any, position: any) => {
    if (isTutorialMode) return
    const adBarTime = position.x / unitLength
    const newClipStartTime = Number(parseFloat(`${adBarTime}`).toFixed(2))
    if (Number(newClipStartTime) >= 0.02 && newClipStartTime < videoLength) {
      if (clipPlaybackType === 'inline') {
        if (newClipStartTime + clipDuration <= videoLength) {
          updateStartTimeNDraggablePosition(newClipStartTime)
        } else {
          toast.error(
            'Audio Clip cannot extend beyond the timeline. Change to extended mode or adjust the start time.',
          )
        }
      } else {
        if (newClipStartTime < videoLength)
          updateStartTimeNDraggablePosition(newClipStartTime)
      }
    } else {
      toast.error('Audio Clip must be positioned within the video timeline.')
    }
  }

  const handleLeftNudgeClick = (e: any) => {
    const newClipStartTime = (parseFloat(`${clipStartTime}`) - 0.25).toFixed(2)
    if (Number(newClipStartTime) >= 0.02) {
      updateStartTimeNDraggablePosition(newClipStartTime)
      toast.success(`Moved clip earlier by 0.25 seconds`)
    } else {
      toast.warning('Cannot move clip before 0.02 seconds')
    }
  }

  const handleRightNudgeClick = (e: any) => {
    const newClipStartTime = Number(
      (parseFloat(`${clipStartTime}`) + 0.25).toFixed(2),
    )
    if (clipPlaybackType === 'inline') {
      if (newClipStartTime + clipDuration <= videoLength) {
        updateStartTimeNDraggablePosition(newClipStartTime)
        toast.success(`Moved clip later by 0.25 seconds`)
      } else {
        toast.error(
          'Cannot move inline clip beyond timeline. Change to extended mode first.',
        )
      }
    } else {
      if (newClipStartTime < videoLength) {
        updateStartTimeNDraggablePosition(newClipStartTime)
        toast.success(`Moved clip later by 0.25 seconds`)
      } else {
        toast.warning('Cannot move clip beyond video end time')
      }
    }
  }

  const updateStartTimeNDraggablePosition = (newClipStartTime: any) => {
    setClipStartTime(newClipStartTime)
    setAdDraggablePosition({ x: newClipStartTime * unitLength, y: 0 })
    handleClipStartTimeUpdate(newClipStartTime)
    currentEvent?.seekTo(Number(newClipStartTime), true)
  }

  const handleClipStartTimeUpdate = (updatedClipStartTime: any) => {
    if (isTutorialMode) return
    axios
      .put(
        `${process.env.REACT_APP_YDX_BACKEND_URL}/api/audio-clips/update-clip-start-time/${clipID}`,
        {
          clipStartTime: updatedClipStartTime,
          audioDescriptionId,
          youtubeVideoId,
        },
      )
      .then(() => {
        setUpdateData(!updateData)
      })
      .catch((err) => {
        console.error('Error updating clip start time:', err)
        toast.error('Failed to update clip timing. Please try again.')
      })
  }

  const handlePlaybackTypeUpdate = (e: any) => {
    if (isTutorialMode) return
    const newPlaybackType = e.target.value
    if (
      clipPlaybackType === 'extended' &&
      newPlaybackType === 'inline' &&
      parseFloat(`${initialClipStartTime}`) + clipDuration > videoLength
    ) {
      toast.error(
        'Cannot change to Inline mode: clip would extend beyond timeline. Please adjust start time first.',
      )
      return
    }
    setClipPlayBackType(newPlaybackType)
    axios
      .put(
        `${process.env.REACT_APP_YDX_BACKEND_URL}/api/audio-clips/update-clip-playback-type/${clipID}`,
        { clipPlaybackType: newPlaybackType },
      )
      .then(() => {
        setUpdateData(!updateData)
        toast.success(`Changed to ${newPlaybackType} mode`)
      })
      .catch((err) => {
        console.error('Error updating playback type:', err)
        toast.error('Failed to change playback mode. Please try again.')
      })
  }

  const handleClickSaveClipDescription = async (
    updatedClipDescriptionText: string,
  ) => {
    try {
      if (updatedClipDescriptionText !== initialclipDescriptionText) {
        setShowSpinner(true)
        if (isTutorialMode) {
          setClipDescriptionText(updatedClipDescriptionText)
          setShowSpinner(false)
          return
        }
        await axios.put(
          `${process.env.REACT_APP_YDX_BACKEND_URL}/api/audio-clips/update-clip-description/${clipID}`,
          {
            userId,
            youtubeVideoId,
            clipDescriptionText: updatedClipDescriptionText,
            clipDescriptionType,
            audioDescriptionId,
          },
        )
        setUpdateData(!updateData)
        toast.success('Description saved successfully!')
      }
      if (clipTitle === '' || clipTitle === null) {
        toast.error('Please enter a valid title')
        setShowSpinner(false)
        return
      } else if (clipTitle !== initialClipTitle) {
        await axios.put(
          `${process.env.REACT_APP_YDX_BACKEND_URL}/api/audio-clips/update-clip-title/${clipID}`,
          { adTitle: clipTitle },
        )
      }
    } catch (err: any) {
      if (err.response) toast.error(err.response.data.message)
      else {
        console.error('Error saving clip:', err)
        toast.error('An error occurred. Please try again!')
      }
    } finally {
      setShowSpinner(false)
    }
  }

  useEffect(() => {
    setClipDescriptionText(clip.description_text)
  }, [clip.description_text])

  const handleDescriptionChange = (newDescription: string) => {
    setClipDescriptionText(newDescription)
    setUpdatedDescriptions((prev) => ({
      ...prev,
      [clip.clip_id]: newDescription,
    }))
  }

  const handlePlayPauseYouTubeVideo = () => {
    if (currentState === -1 || currentState === 0 || currentState === 2) {
      currentEvent?.playVideo()
      setIsYoutubeVideoPlaying(true)
    } else if (currentState === 1) {
      currentEvent?.pauseVideo()
      setIsYoutubeVideoPlaying(false)
    }
  }

  const collabEditCheckboxId = `collabEditCheckbox-${clipID}`

  return (
    <>
      <div id={`audio-clip-card-${clipID}`} className="spacing-component">
        <div
          className="component"
          data-tutorial={
            isTutorialMode ? TUTORIAL_TARGETS.clipControls : undefined
          }
        >
          <div className="audio-clip-header">
            {/* Clip Information Section */}
            <div className="clip-info-section">
              <div
                className="ad-title"
                onClick={() => handlePlayAudioClip(initialClipStartTime)}
              >
                Audio Clip {clipSequenceNumber}
              </div>
              <input
                type="text"
                className="ad-title-input"
                placeholder="Enter clip title..."
                disabled={!showEditComponent}
                readOnly
                value={clipTitle}
                onChange={(e) => setClipTitle(e.target.value)}
              />
              <div className="clip-metadata">
                <div className="audio-mode-indicator">
                  <i className={`fa ${audioModeConfig.icon}`}></i>
                  <span>{audioModeConfig.label}</span>
                </div>
                <div>
                  <strong>Type:</strong>{' '}
                  {clipDescriptionType?.charAt(0).toUpperCase()}
                  {clipDescriptionType?.slice(1)}
                </div>
                <div>
                  <strong>Duration:</strong>{' '}
                  {convertSecondsToCardFormat(clipDuration)}
                </div>
              </div>
            </div>

            {/* Nudge Controls */}
            <div
              className="nudge-controls-section"
              data-tutorial={
                isTutorialMode ? TUTORIAL_TARGETS.nudgeControls : undefined
              }
            >
              <div className="nudge-label">Nudge</div>
              <div className="nudge-btns-div">
                <i
                  className="fa fa-chevron-left nudge-icons"
                  onClick={handleLeftNudgeClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleLeftNudgeClick(e)
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Move clip earlier by 0.25 seconds"
                  title="Move earlier by 0.25s"
                />
                <i
                  className="fa fa-chevron-right nudge-icons"
                  onClick={handleRightNudgeClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleRightNudgeClick(e)
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Move clip later by 0.25 seconds"
                  title="Move later by 0.25s"
                />
              </div>
            </div>

            {/* Timeline Section */}
            <div
              className="timeline-section"
              data-tutorial={
                isTutorialMode ? TUTORIAL_TARGETS.aiClipType : undefined
              }
            >
              <div className="component-timeline-div">
                <div className="ad-draggable-div">
                  <Draggable
                    axis="x"
                    defaultPosition={{ x: 0, y: 0 }}
                    position={adDraggablePosition}
                    onStop={stopADBar}
                    bounds="parent"
                  >
                    {clipPlaybackType === 'inline' ? (
                      <div
                        className="ad-timestamp-div"
                        title={`${convertSecondsToCardFormat(
                          initialClipStartTime,
                        )} - ${audioModeConfig.label}`}
                        style={{
                          width: adDraggableWidth,
                          height: '20px',
                          backgroundColor: 'var(--inline-color)',
                        }}
                      />
                    ) : (
                      <div
                        className="ad-timestamp-div"
                        title={`${convertSecondsToCardFormat(
                          initialClipStartTime,
                        )} - ${audioModeConfig.label}`}
                        style={{
                          width: '3px',
                          height: '20px',
                          backgroundColor: 'var(--extended-color)',
                        }}
                      />
                    )}
                  </Draggable>
                </div>
              </div>
              <div className="playback-type-controls">
                <div className="playback-type-option">
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`playback-${clipSequenceNumber}`}
                    id={`inline-${clipID}`}
                    value="inline"
                    checked={clipPlaybackType === 'inline'}
                    onChange={handlePlaybackTypeUpdate}
                  />
                  <label
                    htmlFor={`inline-${clipID}`}
                    className="inline-extended-radio inline-bg"
                    style={{
                      width: '95px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '40px',
                    }}
                  >
                    <span className="inline-extended-label">Inline</span>
                  </label>
                </div>
                <div className="playback-type-option">
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`playback-${clipSequenceNumber}`}
                    id={`extended-${clipID}`}
                    value="extended"
                    checked={clipPlaybackType === 'extended'}
                    onChange={handlePlaybackTypeUpdate}
                  />
                  <label
                    htmlFor={`extended-${clipID}`}
                    className="inline-extended-radio extended-bg"
                    style={{
                      width: '95px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span className="inline-extended-label">Extended</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Expand/Collapse */}
            <div className="expand-collapse-section">
              {showEditComponent ? (
                <i
                  className="fa fa-chevron-up"
                  onClick={() => setEditComponentToggleFunc(clipID, false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setEditComponentToggleFunc(clipID, false)
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Collapse detailed editing"
                  title="Collapse detailed editing"
                />
              ) : (
                <i
                  className="fa fa-chevron-down"
                  onClick={() => setEditComponentToggleFunc(clipID, true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setEditComponentToggleFunc(clipID, true)
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label="Expand detailed editing"
                  title="Expand detailed editing"
                />
              )}
            </div>
          </div>

          <div className="description-preview">{descriptionDisplay}</div>

          {showEditComponent && (
            <EditClip
              handleClipStartTimeUpdate={handleClipStartTimeUpdate}
              userId={userId}
              youtubeVideoId={youtubeVideoId}
              clipCreatedAt={clipCreatedAt}
              clipId={clipID}
              clipDescriptionType={clipDescriptionType ?? ''}
              initialClipDescriptionText={initialclipDescriptionText ?? ''}
              clipPlaybackType={clipPlaybackType}
              clipStartTime={clipStartTime}
              clipDuration={clipDuration}
              isRecorded={isRecorded ?? false}
              clipAudioPath={clipAudioPath}
              currentTime={currentTime}
              updateData={updateData}
              setUpdateData={setUpdateData}
              videoLength={videoLength}
              setShowSpinner={setShowSpinner}
              audioDescriptionId={audioDescriptionId}
              fetchUserVideoData={fetchUserVideoData}
              setNeedRefresh={setNeedRefresh}
              isPreview={isPreview}
              handleClickSaveClipDescription={handleClickSaveClipDescription}
              setUndoDeletedClip={setUndoDeletedClip}
              setClipDescText={handleDescriptionChange}
              isTutorialMode={isTutorialMode}
              descriptionVolume={descriptionVolume}
            />
          )}
        </div>
      </div>

      {!isPreview && (
        <div className="bottom-row-controls">
          <div />

          <button
            className={`ydx-button ${
              isYoutubeVideoPlaying
                ? 'ydx-button--secondary'
                : 'ydx-button--primary'
            } video-control-btn`}
            onClick={handlePlayPauseYouTubeVideo}
          >
            <i
              className={`fa ${isYoutubeVideoPlaying ? 'fa-pause' : 'fa-play'}`}
            />
            {isYoutubeVideoPlaying ? 'Pause' : 'Play'} Video with Description
          </button>

          {!isPublished ? (
            <div className="enroll-publish-group">
              <span
                data-tutorial={
                  isTutorialMode ? TUTORIAL_TARGETS.collabCheckbox : undefined
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <input
                  type="checkbox"
                  checked={enrollInCollabEdit}
                  onChange={(e) => {
                    if (!isTutorialMode) setEnrollInCollabEdit(e.target.checked)
                  }}
                  id={collabEditCheckboxId}
                  className="form-check-input"
                  data-tutorial={
                    isTutorialMode
                      ? TUTORIAL_TARGETS.collabCheckboxInput
                      : undefined
                  }
                />
                <label
                  htmlFor={collabEditCheckboxId}
                  className="form-check-label text-white"
                  style={{ marginBottom: 0 }}
                >
                  Enroll in Collaborative Editing
                </label>
              </span>
              <button
                type="button"
                className="btn publish-bg text-white ydx-button"
                data-tutorial={
                  isTutorialMode ? TUTORIAL_TARGETS.publishBtn : undefined
                }
                onClick={() => {
                  if (!isTutorialMode) setIsPublishModal(true)
                }}
              >
                <i className="fa fa-upload" /> Publish
              </button>
              <ModalComponent
                id={`publishModal-${clipID}`}
                title="Publish"
                text="Are you sure you want to publish this audio description?"
                modalTask={onPublish}
                show={isPublishModal}
                handleClose={() => setIsPublishModal(false)}
              />
            </div>
          ) : (
            <div />
          )}
        </div>
      )}
    </>
  )
}

export default AudioClip
